import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { Dayjs } from "dayjs";
import iconGridAlt from "../../../assets/icon-grid-alt.svg";
import iconList from "../../../assets/icon-list.svg";
import { DatePickerCalendarAlternateIcon } from "../../../components/shared/DatePickerCalendarAlternateIcon";
import { FilterBarDateRangePopover } from "../../../components/RegulationsTable/FilterBar/FilterBarDateRangePopover";
import { InfiniteScrollConceptAutocomplete } from "../../../components/RegulationsTable/FilterBar/InfiniteScrollConceptAutocomplete";
import { aiPlanFilterBarSx } from "../../../constants/regulationDetailsNavTabStyles";
import type { ConceptValue } from "../../../services/regulationsService";
import {
  formatPlanLabel,
  type ObjectAnalysisPlan,
} from "./objectAnalysisData";

export type ObjectAnalysisView = "grid" | "list";

/** Fixed widths so selected chips or a picked date range never resize the bar. */
const planFieldSx = {
  flex: "0 0 auto",
  width: 300,
  maxWidth: "100%",
} as const;

const dateFieldSx = {
  flex: "0 0 auto",
  width: 240,
  maxWidth: "100%",
} as const;

type ObjectAnalysisFilterBarProps = Readonly<{
  plans: readonly ObjectAnalysisPlan[];
  plansLoading: boolean;
  planIds: string[];
  onPlanIdsChange: (planIds: string[]) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onDateRangeChange: (from: Dayjs | null, to: Dayjs | null) => void;
  view: ObjectAnalysisView;
  onViewChange: (view: ObjectAnalysisView) => void;
}>;

function formatDateRangeDisplay(
  fromDate: Dayjs | null,
  toDate: Dayjs | null,
): string {
  if (fromDate && toDate) {
    return `${fromDate.format("DD MMM YYYY")} - ${toDate.format("DD MMM YYYY")}`;
  }
  if (fromDate) return `From: ${fromDate.format("DD MMM YYYY")}`;
  if (toDate) return `To: ${toDate.format("DD MMM YYYY")}`;
  return "";
}

export default function ObjectAnalysisFilterBar({
  plans,
  plansLoading,
  planIds,
  onPlanIdsChange,
  fromDate,
  toDate,
  onDateRangeChange,
  view,
  onViewChange,
}: ObjectAnalysisFilterBarProps) {
  const [planSearch, setPlanSearch] = useState("");
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);

  const planOptions = useMemo<ConceptValue[]>(
    () =>
      plans.map((plan) => ({
        id: plan.id,
        code: plan.id,
        name: formatPlanLabel(plan),
      })),
    [plans],
  );

  const selectedPlans = useMemo(
    () => planOptions.filter((option) => planIds.includes(option.code)),
    [planIds, planOptions],
  );

  const filteredPlanOptions = useMemo(() => {
    const search = planSearch.trim().toLowerCase();
    if (!search) return planOptions;
    return planOptions.filter((option) =>
      option.name.toLowerCase().includes(search),
    );
  }, [planOptions, planSearch]);

  // Drop stale selections when active plans reload.
  useEffect(() => {
    if (plansLoading || planIds.length === 0) return;
    const valid = new Set(plans.map((plan) => plan.id));
    const next = planIds.filter((id) => valid.has(id));
    if (next.length !== planIds.length) onPlanIdsChange(next);
  }, [onPlanIdsChange, planIds, plans, plansLoading]);

  const handlePlanChange = useCallback(
    (_event: unknown, nextValue: ConceptValue[]) => {
      onPlanIdsChange(nextValue.map((option) => option.code));
    },
    [onPlanIdsChange],
  );

  const handlePlanInputChange = useCallback(
    (_event: unknown, nextValue: string) => setPlanSearch(nextValue),
    [],
  );

  return (
    <Box sx={{ ...aiPlanFilterBarSx, justifyContent: "space-between" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <InfiniteScrollConceptAutocomplete
          label=""
          value={selectedPlans}
          onChange={handlePlanChange}
          options={filteredPlanOptions}
          inputValue={planSearch}
          onInputChange={handlePlanInputChange}
          loading={plansLoading}
          hideSelectedWhenSearching
          selectedVisibilityMode="matchWhenSearchingAllWhenEmpty"
          maxSelection={Math.max(plans.length, 1)}
          limitTags={2}
          hideSelectionCount
          placeholder="Select the Object A & Object B"
          sx={planFieldSx}
        />

        <TextField
          size="small"
          value={formatDateRangeDisplay(fromDate, toDate)}
          placeholder="Start date - End date"
          onClick={(event) => setDateAnchorEl(event.currentTarget)}
          sx={dateFieldSx}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -1 }}>
                  <IconButton
                    aria-label="Select date range"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDateAnchorEl(event.currentTarget);
                    }}
                    sx={{
                      borderLeft: "1px solid #d1d5db",
                      borderRadius: 0,
                      px: 1,
                    }}
                  >
                    <DatePickerCalendarAlternateIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <FilterBarDateRangePopover
          open={Boolean(dateAnchorEl)}
          anchorEl={dateAnchorEl}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setDateAnchorEl(null)}
          onDateRangeChange={onDateRangeChange}
        />
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={view}
        onChange={(_event, nextView: ObjectAnalysisView | null) => {
          if (nextView) onViewChange(nextView);
        }}
        sx={{
          flexShrink: 0,
          height: 32,
          border: "1px solid #E5E7EB",
          borderRadius: "6px",
          overflow: "hidden",
          bgcolor: "#ffffff",
          "& .MuiToggleButton-root": {
            border: "none",
            borderRadius: 0,
            px: "10px",
            bgcolor: "transparent",
            "&:hover": { bgcolor: "#F5F5F5" },
            "&.Mui-selected": {
              bgcolor: "#E0E0E0",
              "&:hover": { bgcolor: "#E0E0E0" },
            },
          },
        }}
      >
        <ToggleButton value="grid" aria-label="Grid view">
          <Box
            component="img"
            src={iconGridAlt}
            alt=""
            sx={{ width: 18, height: 18, display: "block" }}
          />
        </ToggleButton>
        <ToggleButton value="list" aria-label="List view">
          <Box
            component="img"
            src={iconList}
            alt=""
            sx={{ width: 18, height: 18, display: "block" }}
          />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
